import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { palette } from '../styles/tokens';
import { roleColor } from '../styles/tokens';
import { canonicalMoney, sumMoney } from '../lib/money';
import type {
  DashboardMetrics,
  AttentionItem,
  CrewMember,
  RevenueDataPoint,
  ScheduleStaffRow,
  UserRole,
  EmploymentType,
} from '../types';

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function dayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { weekday: 'short' });
}

function timeToHourOffset(time: string, baseHour: number): number {
  const [h, m] = time.split(':').map(Number);
  return (h - baseHour) + (m / 60);
}

function formatTime12(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

interface DashboardData {
  metrics: DashboardMetrics | null;
  attentionItems: AttentionItem[];
  crewToday: CrewMember[];
  revenueData: RevenueDataPoint[];
  scheduleData: ScheduleStaffRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDashboard(businessId: string | undefined): DashboardData {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  const [crewToday, setCrewToday] = useState<CrewMember[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleStaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const today = new Date();
    const todayStr = formatDate(today);
    const weekStart = startOfWeek(today);
    const weekStartStr = formatDate(weekStart);
    const lastWeekStart = new Date(weekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekStartStr = formatDate(lastWeekStart);
    const weekEndStr = formatDate(new Date(weekStart.getTime() + 6 * 86400000));
    const nextWeekStr = formatDate(new Date(today.getTime() + 7 * 86400000));

    try {
      const [
        paidThisWeek,
        paidLastWeek,
        outstandingResult,
        overdueInvoices,
        entriesToday,
        checkInsToday,
        usersResult,
        entriesThisWeek,
        vehiclesResult,
        pendingBookings,
        paidByDay,
        scheduleEntriesToday,
        coverageGapEntries,
      ] = await Promise.all([
        // Revenue this week — invoices.total is the real column
        supabase
          .from('invoices')
          .select('total')
          .eq('business_id', businessId)
          .eq('status', 'paid')
          .gte('paid_at', weekStartStr),

        // Revenue last week (for delta)
        supabase
          .from('invoices')
          .select('total')
          .eq('business_id', businessId)
          .eq('status', 'paid')
          .gte('paid_at', lastWeekStartStr)
          .lt('paid_at', weekStartStr),

        // Outstanding invoices
        supabase
          .from('invoices')
          .select('total, status, due_date')
          .eq('business_id', businessId)
          .in('status', ['sent', 'overdue']),

        // Overdue invoices for attention list
        supabase
          .from('invoices')
          .select('id, invoice_number, client_name, total, due_date')
          .eq('business_id', businessId)
          .eq('status', 'overdue')
          .order('due_date', { ascending: true })
          .limit(5),

        // Today's schedule entries — assigned_to is an array, status tells us coverage state
        supabase
          .from('schedule_entries')
          .select('id, assigned_to, start_at, end_at, status, type, title, location_name')
          .eq('business_id', businessId)
          .gte('start_at', `${todayStr}T00:00:00`)
          .lte('start_at', `${todayStr}T23:59:59`),

        // Today's check-ins — real columns: user_id, type, timestamp, shift_id
        supabase
          .from('check_ins')
          .select('user_id, type, timestamp, shift_id')
          .eq('business_id', businessId)
          .eq('type', 'check_in')
          .gte('timestamp', `${todayStr}T00:00:00`),

        // All users for this business
        supabase
          .from('users')
          .select('id, first_name, last_name, role, employment_type, hourly_rate')
          .eq('business_id', businessId)
          .eq('status', 'active'),

        // Entries this week (for overtime calc) — shifts only
        supabase
          .from('schedule_entries')
          .select('assigned_to, start_at, end_at')
          .eq('business_id', businessId)
          .eq('type', 'shift')
          .gte('start_at', `${weekStartStr}T00:00:00`)
          .lte('start_at', `${weekEndStr}T23:59:59`)
          .in('status', ['scheduled', 'in_progress', 'completed']),

        // Vehicles with maintenance info — real columns: name, next_maintenance_date, last_maintenance_date
        supabase
          .from('vehicles')
          .select('id, name, next_maintenance_date, last_maintenance_date, status')
          .eq('business_id', businessId)
          .neq('status', 'retired'),

        // Pending bookings — no service_type, use client_name + source
        supabase
          .from('bookings')
          .select('id, client_name, source')
          .eq('business_id', businessId)
          .eq('status', 'pending')
          .limit(5),

        // Revenue by day for chart
        supabase
          .from('invoices')
          .select('total, paid_at')
          .eq('business_id', businessId)
          .eq('status', 'paid')
          .gte('paid_at', weekStartStr)
          .lte('paid_at', weekEndStr),

        // Schedule entries for mini schedule — all non-cancelled entries today
        supabase
          .from('schedule_entries')
          .select('id, assigned_to, start_at, end_at, status, type, title, location_name, all_day')
          .eq('business_id', businessId)
          .gte('start_at', `${todayStr}T00:00:00`)
          .lte('start_at', `${todayStr}T23:59:59`)
          .in('status', ['scheduled', 'in_progress', 'completed']),

        // Coverage gaps = cancelled entries in next 7 days
        supabase
          .from('schedule_entries')
          .select('id, start_at, end_at, status, type')
          .eq('business_id', businessId)
          .eq('status', 'cancelled')
          .gte('start_at', `${todayStr}T00:00:00`)
          .lte('start_at', `${nextWeekStr}T23:59:59`),
      ]);

      // ── Metrics ──
      // Rule #34: canonical money sums; Number() at consumer boundary.
      const revenueThisWeekTotal = Number(sumMoney((paidThisWeek.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      const revenueLastWeekTotal = Number(sumMoney((paidLastWeek.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      const outstandingTotal = Number(sumMoney((outstandingResult.data ?? []).map((i: { total: number | null }) => canonicalMoney(i.total ?? 0))));
      const outstandingItems = outstandingResult.data ?? [];
      const overdueItems = outstandingItems.filter(i => i.status === 'overdue');
      const overdueCount = overdueItems.length;
      const invoicePendingCount = outstandingItems.filter(i => i.status === 'sent').length;
      const oldestOverdueDays = overdueItems.length > 0
        ? Math.max(...overdueItems.map(i => Math.floor((Date.now() - new Date(i.due_date).getTime()) / 86400000)))
        : 0;

      // Coverage: total entries today vs those in good standing
      const todayEntries = entriesToday.data ?? [];
      const coverageTotal = todayEntries.length;
      const activeStatuses = ['scheduled', 'in_progress', 'completed'];
      const coverageFilled = todayEntries.filter(s => activeStatuses.includes(s.status)).length;
      const firstGap = todayEntries.find(s => !activeStatuses.includes(s.status));
      const coverageGapTime = firstGap ? formatTime12(firstGap.start_at) : null;

      // Next payroll: next Friday
      const daysUntilFriday = (5 - today.getDay() + 7) % 7 || 7;
      const nextFriday = new Date(today);
      nextFriday.setDate(today.getDate() + daysUntilFriday);

      // Estimate payroll from this week's shift hours × each user's hourly_rate
      const userRateMap = new Map<string, number>();
      for (const u of usersResult.data ?? []) {
        userRateMap.set(u.id, u.hourly_rate ?? 25);
      }
      let estimatedPayroll = 0;
      for (const s of entriesThisWeek.data ?? []) {
        const startH = timeToHourOffset(s.start_at?.split('T')[1] ?? '0:0', 0);
        const endH = timeToHourOffset(s.end_at?.split('T')[1] ?? '0:0', 0);
        const hours = endH - startH;
        const uid = Array.isArray(s.assigned_to) ? s.assigned_to[0] : null;
        const rate = uid ? (userRateMap.get(uid) ?? 25) : 25;
        estimatedPayroll += Math.max(0, hours) * rate;
      }
      estimatedPayroll = Math.round(estimatedPayroll);

      setMetrics({
        revenueThisWeek: revenueThisWeekTotal,
        revenueLastWeek: revenueLastWeekTotal,
        outstanding: outstandingTotal,
        overdueCount,
        pendingCount: invoicePendingCount,
        oldestOverdueDays,
        coverageFilled,
        coverageTotal,
        coverageGapTime,
        nextPayrollDate: nextFriday.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
        nextPayrollEstimate: estimatedPayroll,
      });

      // ── Attention items ──
      const attention: AttentionItem[] = [];

      for (const inv of overdueInvoices.data ?? []) {
        const daysOverdue = Math.floor((Date.now() - new Date(inv.due_date).getTime()) / 86400000);
        attention.push({
          id: `inv-${inv.id}`,
          type: 'overdue_invoice',
          urgency: 1,
          icon: '\uD83D\uDCB0',
          iconBg: palette.redWash,
          text: `Invoice #${inv.invoice_number} overdue — ${inv.client_name ?? 'Unknown'}`,
          meta: `$${inv.total} · ${daysOverdue} days past due`,
          action: 'Send reminder',
        });
      }

      // Coverage gaps = cancelled entries in next 7 days
      for (const gap of coverageGapEntries.data ?? []) {
        const gapDate = gap.start_at?.split('T')[0] ?? '';
        attention.push({
          id: `gap-${gap.id}`,
          type: 'coverage_gap',
          urgency: 2,
          icon: '\uD83D\uDCC5',
          iconBg: palette.redWash,
          text: `Cancelled ${gap.type ?? 'entry'} — ${new Date(gapDate + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short' })} ${formatTime12(gap.start_at)}–${formatTime12(gap.end_at)}`,
          meta: `Entry cancelled · may need coverage`,
          action: 'Find coverage',
        });
      }

      // Overtime warnings
      const userHours = new Map<string, number>();
      for (const s of entriesThisWeek.data ?? []) {
        const startH = timeToHourOffset(s.start_at?.split('T')[1] ?? '0:0', 0);
        const endH = timeToHourOffset(s.end_at?.split('T')[1] ?? '0:0', 0);
        const hours = endH - startH;
        const uid = Array.isArray(s.assigned_to) ? s.assigned_to[0] : null;
        if (uid) userHours.set(uid, (userHours.get(uid) ?? 0) + Math.max(0, hours));
      }
      const usersMap = new Map((usersResult.data ?? []).map(u => [u.id, u]));
      for (const [userId, hours] of userHours) {
        if (hours >= 38) {
          const u = usersMap.get(userId);
          if (u) {
            attention.push({
              id: `ot-${userId}`,
              type: 'overtime_warning',
              urgency: 3,
              icon: '\u23F1',
              iconBg: palette.amberWash,
              text: `${u.first_name} ${u.last_name.charAt(0)}. approaching overtime`,
              meta: `${Math.round(hours)}h this week · limit 44h`,
              action: 'View',
            });
          }
        }
      }

      // Vehicle maintenance overdue — use next_maintenance_date
      for (const v of vehiclesResult.data ?? []) {
        if (v.next_maintenance_date && new Date(v.next_maintenance_date) < today) {
          const daysOverdue = Math.floor((Date.now() - new Date(v.next_maintenance_date).getTime()) / 86400000);
          attention.push({
            id: `veh-${v.id}`,
            type: 'maintenance_due',
            urgency: 4,
            icon: '\uD83D\uDD27',
            iconBg: palette.amberWash,
            text: `${v.name} maintenance overdue`,
            meta: `Due ${daysOverdue} days ago`,
            action: 'Schedule',
          });
        }
      }

      // Pending bookings
      const pendingCount = (pendingBookings.data ?? []).length;
      if (pendingCount > 0) {
        attention.push({
          id: 'bookings-pending',
          type: 'pending_booking',
          urgency: 5,
          icon: '\uD83D\uDCCB',
          iconBg: palette.blueWash,
          text: `${pendingCount} new booking${pendingCount > 1 ? 's' : ''} need${pendingCount === 1 ? 's' : ''} confirmation`,
          meta: `Received recently`,
          action: 'Review',
        });
      }

      attention.sort((a, b) => a.urgency - b.urgency);
      setAttentionItems(attention.slice(0, 5));

      // ── Crew today ──
      // Build check-in set: users who have checked in today
      const checkedInUsers = new Set<string>();
      for (const ci of checkInsToday.data ?? []) {
        checkedInUsers.add(ci.user_id);
      }

      // Map entries to users (first assignee)
      const entryByUser = new Map<string, typeof todayEntries[number]>();
      for (const entry of todayEntries) {
        if (activeStatuses.includes(entry.status)) {
          const uid = Array.isArray(entry.assigned_to) ? entry.assigned_to[0] : null;
          if (uid && !entryByUser.has(uid)) entryByUser.set(uid, entry);
        }
      }

      const now = new Date();
      const crew: CrewMember[] = [];
      for (const u of usersResult.data ?? []) {
        const entry = entryByUser.get(u.id);
        const hasCheckedIn = checkedInUsers.has(u.id);

        let status: CrewMember['status'] = 'off';
        let shiftTime = '—';

        if (entry) {
          shiftTime = `${formatTime12(entry.start_at)} – ${formatTime12(entry.end_at)}`;
          const entryStart = new Date(entry.start_at);

          if (hasCheckedIn || entry.status === 'in_progress') {
            status = 'working';
          } else if (now > entryStart) {
            status = 'late';
          } else {
            status = 'scheduled';
          }
        }

        crew.push({
          id: u.id,
          name: `${u.first_name} ${u.last_name.charAt(0)}.`,
          initials: getInitials(u.first_name, u.last_name),
          role: u.role as UserRole,
          employmentType: (u.employment_type ?? 'employee') as EmploymentType,
          status,
          shiftTime,
        });
      }

      const statusOrder = { working: 0, late: 1, scheduled: 2, off: 3 };
      crew.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
      setCrewToday(crew);

      // ── Revenue chart ──
      const dayTotals = new Map<string, number>();
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(weekStart.getDate() + i);
        dayTotals.set(formatDate(d), 0);
      }
      for (const inv of paidByDay.data ?? []) {
        if (inv.paid_at) {
          const dayKey = inv.paid_at.split('T')[0];
          dayTotals.set(dayKey, (dayTotals.get(dayKey) ?? 0) + (inv.total ?? 0));
        }
      }
      const chartData: RevenueDataPoint[] = [];
      for (const [dateKey, total] of dayTotals) {
        chartData.push({ label: dayLabel(dateKey), value: Math.round(total) });
      }
      setRevenueData(chartData);

      // ── Schedule mini ──
      const BASE_HOUR = 7;
      const scheduleStaff: ScheduleStaffRow[] = [];

      for (const entry of scheduleEntriesToday.data ?? []) {
        if (entry.all_day) continue;
        const uid = Array.isArray(entry.assigned_to) ? entry.assigned_to[0] : null;
        const u = uid ? usersMap.get(uid) : null;
        if (!u) continue;

        const staffName = `${u.first_name} ${u.last_name.charAt(0)}.`;
        const startTime = entry.start_at?.split('T')[1] ?? '09:00';
        const endTime = entry.end_at?.split('T')[1] ?? '17:00';
        const block = {
          start: timeToHourOffset(startTime, BASE_HOUR),
          end: timeToHourOffset(endTime, BASE_HOUR),
          label: `${formatTime12(entry.start_at)} – ${formatTime12(entry.end_at)}`,
          type: (entry.type === 'job' ? 'job' : 'shift') as 'shift' | 'job',
        };

        const empType = u.employment_type ?? 'employee';
        const color = empType === 'contractor' ? palette.amber
          : empType === 'operator' ? palette.blue
          : roleColor[u.role as keyof typeof roleColor] ?? palette.g300;

        const existingRow = scheduleStaff.find(r => r.name === staffName);
        if (existingRow) {
          existingRow.blocks.push(block);
        } else {
          scheduleStaff.push({
            name: staffName,
            initials: getInitials(u.first_name, u.last_name),
            roleColor: color,
            blocks: [block],
          });
        }
      }

      setScheduleData(scheduleStaff);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['schedule_entries', 'invoices', 'check_ins'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  return {
    metrics,
    attentionItems,
    crewToday,
    revenueData,
    scheduleData,
    loading,
    error,
    refresh: fetchAll,
  };
}
